/**
 * Today's "Records waiting on you" (13-29), as pure functions: the register records waiting on the signed-in person,
 * grouped, ordered and worded for the section under the queue.
 *
 *   - `groupWaiting(records, actor, now, ctx)`: membership comes from 13-22's `recordsWaitingOn` and nothing else, so
 *     the section can never show a record the person can't act on (the D-13-05-3 "queue equals the permission" rule).
 *     Groups, in order: Decisions due, Notices due, To triage, To close or approve, Due or past due. A record appears
 *     once, in its first group, with every reason it is waiting. Inside a group: past due first, then the earliest
 *     due, then the oldest (D-13-29-1).
 *   - `lensRegisters(actor)`: the registers the lens can touch, so the hook reads only those (one list read each).
 *
 * Due lines read `D MMM YYYY`, an instant with its Melbourne time (D-13-L-10); past due is flagged for amber, never
 * red (D-13-15-4). An item's label is the record's own `Title` as the register holds it; nothing here adds a name.
 * Pure: no React, no Graph, no `Date.now()`.
 */

import { REGISTERS } from '../config/registers';
import { RECORD_REGISTERS } from '../config/fieldset/registerFields';
import type { Actor } from '../types/actionRegister';
import type { RecordRegister, RecordRow } from '../types/records';
import { isOversight } from './actionAuthority';
import { formatDay, formatInstant, melbourneWallToInstant } from './fieldValues';
import {
  isSahDecider,
  isWorkSafeDelegate,
  recordsWaitingOn,
  type ReportabilityDraftView,
  type WaitingContext,
  type WaitingDue,
  type WaitingKind,
} from './recordAuthority';

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export type WaitingGroupKey = 'decisions' | 'notices' | 'triage' | 'close' | 'due';

/** The section's groups, in display order (the plan's order). */
export const GROUP_ORDER: readonly WaitingGroupKey[] = ['decisions', 'notices', 'triage', 'close', 'due'];

export const GROUP_HEADINGS: Readonly<Record<WaitingGroupKey, string>> = {
  decisions: 'Decisions due',
  notices: 'Notices due',
  triage: 'To triage',
  close: 'To close or approve',
  due: 'Due or past due',
};

/** Which group each lens kind belongs to (D-13-29-1). */
export const GROUP_OF_KIND: Readonly<Record<WaitingKind, WaitingGroupKey>> = {
  // 13.2: completing the report is the step before triage, so it waits under To triage.
  'to-complete': 'triage',
  'reportability-pending': 'decisions',
  'notice-due': 'notices',
  'whs-day-due': 'notices',
  'to-triage': 'triage',
  'client-id-to-add': 'triage',
  'whs-to-triage': 'triage',
  'hazard-open': 'triage',
  'to-close': 'close',
  'to-approve': 'close',
  'preparer-unknown': 'close',
  'handling-due': 'due',
};

/** Why the record is waiting, in plain words (one per kind). */
export const REASON_OF_KIND: Readonly<Record<WaitingKind, string>> = {
  'to-complete': 'Complete the report',
  'to-triage': 'New: to triage',
  'handling-due': 'Handling due',
  'client-id-to-add': 'Client id to add',
  'to-close': 'Ready to close',
  'to-approve': 'Waiting for your approval',
  'preparer-unknown': 'Waiting for approval (the record page checks who prepared it)',
  'reportability-pending': 'Reportability to decide',
  'notice-due': 'Notice to record',
  'whs-to-triage': 'WHS questions to answer',
  'whs-day-due': 'WHS duty due',
  'hazard-open': 'Hazard type to record',
};

/** The record page panel each kind opens (`RecordPage` anchors each slot as `#panel-<key>`). */
const PANEL_OF_KIND: Readonly<Record<WaitingKind, string>> = {
  // 13.2: the Complete panel (13.2-09 builds it under this slot key).
  'to-complete': 'complete',
  'to-triage': 'triage',
  'client-id-to-add': 'triage',
  'handling-due': 'handling',
  'to-close': 'closure',
  'to-approve': 'closure',
  'preparer-unknown': 'closure',
  'reportability-pending': 'reportability',
  'notice-due': 'notification',
  'whs-to-triage': 'whs',
  'whs-day-due': 'whs',
  'hazard-open': 'whs',
};

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

/** One due line: "Resolve by 10 Oct 2026", "NDIS 24-hour notice due 13 Oct 2026, 9:00 am". */
export interface DueLine {
  text: string;
  /** Past due: shown amber, never red (D-13-15-4). */
  past: boolean;
  /** 13.2 (D-13.2-P-5): from the CM's draft, not a decision; its text says "If confirmed: …". Display only. */
  provisional?: true;
}

export interface QueueItem {
  ref: string;
  register: RecordRegister;
  /** The register's list title ("Incidents", "Risk Register"). */
  registerLabel: string;
  /** The record's `Title` as the register holds it, or its ref when it has none. */
  label: string;
  /** Every lens kind the record is waiting for, in `WAITING_KINDS` order. */
  kinds: WaitingKind[];
  /** The kinds in words. */
  reasons: string[];
  /** Every due across the kinds, earliest first. */
  dues: DueLine[];
  /** Any due has passed. */
  pastDue: boolean;
  /** `/record/<ref>#panel-<slot>`: the record page, at the panel the first group asks for. */
  href: string;
  /** 13.2 (D-13.2-P-5): the CM's draft reportability view, on a decision waiting on this person. */
  draft?: ReportabilityDraftView;
}

export interface WaitingGroup {
  key: WaitingGroupKey;
  heading: string;
  items: QueueItem[];
}

/** A due's instant for ordering: an instant as itself, a day as the end of that Melbourne day; none last. */
function dueMs(due: WaitingDue): number {
  if (due.at === null) return Number.POSITIVE_INFINITY;
  if (due.precision === 'instant') {
    const ms = Date.parse(due.at);
    return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
  }
  const end = melbourneWallToInstant(due.at, '23:59');
  return end.ok ? Date.parse(end.value) : Number.POSITIVE_INFINITY;
}

function dueText(due: WaitingDue): string {
  if (due.at === null) return `${due.label}: no time yet (when Harmony became aware isn't recorded)`;
  return `${due.label} ${due.precision === 'day' ? formatDay(due.at) : formatInstant(due.at)}`;
}

function textOf(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function createdMs(row: RecordRow): number {
  const ms = Date.parse(textOf(row.fields.Created));
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
}

/** The panel an item opens: a WorkSafe-only notice opens the WorkSafe panel, every other notice the notification one. */
function panelFor(kind: WaitingKind, dues: readonly WaitingDue[]): string {
  if (kind === 'notice-due' && dues.length > 0 && dues.every((d) => d.notice === 'worksafe')) return 'worksafe';
  return PANEL_OF_KIND[kind];
}

interface Built {
  item: QueueItem;
  group: WaitingGroupKey;
  earliest: number;
  created: number;
  id: number;
}

// ---------------------------------------------------------------------------
// groupWaiting
// ---------------------------------------------------------------------------

/**
 * The records waiting on `actor`, grouped (only non-empty groups, in `GROUP_ORDER`). `records` are the rows the hook
 * read; `ctx` carries each Evaluating incident's preparer when the preparer read gave one (D-13-L-19).
 */
export function groupWaiting(
  records: readonly RecordRow[],
  actor: Actor,
  now: Date,
  ctx: WaitingContext = {},
): WaitingGroup[] {
  const byRef = new Map<string, { row: RecordRow; kinds: WaitingKind[]; dues: WaitingDue[]; draft?: ReportabilityDraftView }>();
  for (const item of recordsWaitingOn(actor, records, now, ctx)) {
    const entry = byRef.get(item.row.ref) ?? { row: item.row, kinds: [], dues: [] };
    entry.kinds.push(item.kind);
    if (item.draft) entry.draft = item.draft;
    for (const due of item.dues ?? []) {
      if (!entry.dues.some((d) => d.column === due.column && d.at === due.at && d.label === due.label)) entry.dues.push(due);
    }
    byRef.set(item.row.ref, entry);
  }

  const built: Built[] = [];
  for (const { row, kinds, dues, draft } of byRef.values()) {
    const group = GROUP_ORDER.find((g) => kinds.some((k) => GROUP_OF_KIND[k] === g)) as WaitingGroupKey;
    const lead = kinds.find((k) => GROUP_OF_KIND[k] === group) as WaitingKind;
    const leadDues = lead === 'notice-due' ? dues.filter((d) => d.notice !== undefined) : dues;
    const sorted = [...dues].sort((a, b) => dueMs(a) - dueMs(b));
    const label = textOf(row.fields.Title) || row.ref;
    built.push({
      item: {
        ref: row.ref,
        register: row.register,
        registerLabel: REGISTERS[row.register].listTitle,
        label,
        kinds,
        reasons: kinds.map((k) => REASON_OF_KIND[k]),
        dues: sorted.map((d) => (d.provisional ? { text: dueText(d), past: d.past, provisional: true } : { text: dueText(d), past: d.past })),
        pastDue: dues.some((d) => d.past),
        href: `/record/${row.ref}#panel-${panelFor(lead, leadDues)}`,
        ...(draft ? { draft } : {}),
      },
      group,
      earliest: sorted.length > 0 ? dueMs(sorted[0]) : Number.POSITIVE_INFINITY,
      created: createdMs(row),
      id: row.id,
    });
  }

  const order = (a: Built, b: Built) =>
    Number(b.item.pastDue) - Number(a.item.pastDue) ||
    a.earliest - b.earliest ||
    a.created - b.created ||
    a.id - b.id ||
    (a.item.ref < b.item.ref ? -1 : a.item.ref > b.item.ref ? 1 : 0);

  const groups: WaitingGroup[] = [];
  for (const key of GROUP_ORDER) {
    const items = built.filter((b) => b.group === key).sort(order).map((b) => b.item);
    if (items.length > 0) groups.push({ key, heading: GROUP_HEADINGS[key], items });
  }
  return groups;
}

// ---------------------------------------------------------------------------
// The registers the lens reads
// ---------------------------------------------------------------------------

/**
 * The registers `recordsWaitingOn` can return a record from for this actor, in register order: a Care Manager
 * Incidents, Complaints and Feedback; Oversight Incidents, Risk and CI; HR & WHS Incidents and Risk; the SaH decider
 * Incidents. The plan listed all five for Oversight; the lens offers Oversight nothing on Complaints or Feedback, so
 * those two reads are not made (D-13-29-4). `recordQueue.test.ts` checks every offered record is on one of these.
 */
export function lensRegisters(actor: Actor): RecordRegister[] {
  const roles = Array.isArray(actor.roles) ? actor.roles : [];
  const out = new Set<RecordRegister>();
  if (roles.includes('CareManager')) for (const r of ['incidents', 'complaints', 'feedback'] as const) out.add(r);
  if (isOversight(actor)) for (const r of ['incidents', 'risk', 'ci'] as const) out.add(r);
  if (roles.includes('HRWHS')) for (const r of ['incidents', 'risk'] as const) out.add(r);
  if (isSahDecider(actor) || isWorkSafeDelegate(actor)) out.add('incidents');
  return RECORD_REGISTERS.filter((r) => out.has(r));
}

/** The counts the briefing's records line needs (`briefing.recordsLine`). */
export interface WaitingCounts {
  records: number;
  decisions: number;
  notices: number;
  pastDue: number;
}

export function waitingCounts(groups: readonly WaitingGroup[]): WaitingCounts {
  const count = (key: WaitingGroupKey) => groups.find((g) => g.key === key)?.items.length ?? 0;
  const items = groups.flatMap((g) => g.items);
  return { records: items.length, decisions: count('decisions'), notices: count('notices'), pastDue: items.filter((i) => i.pastDue).length };
}
